import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, Play, ChevronRight, CheckCircle2, Zap, Wrench, ShieldCheck, Award, Cog, Factory, Building2, Cpu, HeartPulse, Users, Clock, TrendingUp, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { heroImage as fallbackHeroImage, aboutImage as fallbackAboutImage, heroStats as fallbackHeroStats, capabilities as fallbackCapabilities, industries as fallbackIndustries, clients as fallbackClients, whyUs as fallbackWhyUs, trustBadges as fallbackTrustBadges } from "../data";
import { getSiteContent } from "../cms/siteQueries";

export default function Home() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    let mounted = true;
    getSiteContent()
      .then((data) => {
        if (mounted && data) setContent(data);
      })
      .catch((error) => console.error("Unable to load website content from Sanity:", error));
    return () => { mounted = false; };
  }, []);

  const home = content?.home || {};
  const company = content?.company || {};
  const heroImage = home.heroImage || fallbackHeroImage;
  const aboutImage = home.aboutImage || fallbackAboutImage;
  const heroStats = home.stats || fallbackHeroStats;
  const capabilities = content?.capabilities || fallbackCapabilities;
  const industries = content?.industries || fallbackIndustries;
  const clients = content?.clients || fallbackClients;
  const whyUs = content?.whyUs || fallbackWhyUs;
  const trustBadges = home.trustBadges || fallbackTrustBadges;

  return (
    <div className="bg-white">
      {/* ============== HERO ============== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0a1929]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0a1929]/85 to-transparent" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-40 bg-[#c8102e]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 bg-[#c8102e]/10 border border-[#c8102e]/40 text-[#ff6b7a] px-4 py-1.5 mb-6 text-xs uppercase tracking-[0.2em] font-semibold">
              <Zap className="w-3.5 h-3.5" /> {home.heroBadge || "India’s Fastest-Growing Automation Partner"}
            </div>
            <h1 className="font-black text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-6">
              {home.heroTitleLine1 || "Industrial Automation."}<br />
              <span className="text-[#ff6b7a]">{home.heroTitleLine2 || "Precision Fabrication."}</span><br />
              {home.heroTitleLine3 || "Reliable Service."}
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mb-10 leading-relaxed">
              {home.heroDescription || "HIG Automation India Pvt. Ltd. delivers Industrial Automation, PLC & SCADA Programming, Special Purpose Machines (SPM), Paint Shop Automation, Conveyor Systems, Industrial Fabrication, Control Panels, and Turnkey Engineering Solutions across India."}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="bg-[#c8102e] hover:bg-[#a80d26] text-white rounded-none px-8 py-6 text-sm font-bold tracking-wide uppercase gap-2">
                  Schedule Site Visit <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/services/plc-scada-automation">
                <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0a1929] rounded-none px-8 py-6 text-sm font-bold tracking-wide uppercase">
                  Explore Solutions
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {heroStats.map((s, i) => (
                <div key={i} className="bg-[#0a1929]/80 backdrop-blur p-6">
                  <div className="text-4xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== TRUST BAR ============== */}
      <section className="bg-[#c8102e] text-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
          {trustBadges.map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
              <CheckCircle2 className="w-4 h-4" /> {b}
            </div>
          ))}
        </div>
      </section>

      {/* ============== INTRO / ABOUT ============== */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/3] w-full bg-slate-200 overflow-hidden">
              <img src={aboutImage} alt="HIGA facility" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#c8102e] text-white p-6 max-w-xs hidden md:block">
              <div className="text-4xl font-black leading-none">2025</div>
              <div className="text-xs uppercase tracking-widest mt-2 opacity-90">Founded in Delhi NCR</div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">About HIGA</div>
            <h2 className="font-black text-4xl lg:text-5xl text-[#0a1929] leading-tight mb-6 tracking-tight">
              {home.aboutHeading || "Complete Industrial Automation & Engineering Solutions Under One Roof."}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              {home.aboutParagraph1 || "HIG Automation India Private Limited (HIGA) is a Delhi NCR-based engineering services company specializing in factory automation, pneumatic systems, conveyor systems, fire protection, AHU installations, and precision fabrication."}
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              {home.aboutParagraph2 || "We combine deep technical expertise with hands-on execution — from design and fabrication to installation, commissioning, and maintenance — delivering complete turnkey solutions."}
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="border-l-4 border-[#c8102e] pl-4">
                <div className="font-bold text-[#0a1929] text-lg mb-1">Our Mission</div>
                <p className="text-sm text-slate-600">{home.mission || "Become India’s preferred end-to-end industrial automation partner — known for speed, precision, and after-sales reliability."}</p>
              </div>
              <div className="border-l-4 border-[#0a1929] pl-4">
                <div className="font-bold text-[#0a1929] text-lg mb-1">Our Vision</div>
                <p className="text-sm text-slate-600">{home.vision || "Empower manufacturers with smart, cost-effective automation that improves productivity and reduces downtime — every time."}</p>
              </div>
            </div>

            <Link to="/about">
              <Button className="bg-[#0a1929] hover:bg-[#14213d] text-white rounded-none px-8 py-6 text-sm font-bold tracking-wide uppercase gap-2">
                More About Us <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== CAPABILITIES / PROJECT HIGHLIGHTS ============== */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">Project Highlights</div>
              <h2 className="font-black text-4xl lg:text-5xl text-[#0a1929] tracking-tight leading-tight">What We Deliver</h2>
            </div>
            <p className="text-slate-600 max-w-md">
              {home.capabilitiesIntro || "From complete pipeline turnkey projects to bespoke special purpose machines — executed by our in-house engineering team."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((c, i) => (
              <div key={i} className="group relative overflow-hidden bg-[#0a1929] aspect-[3/4]">
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-110 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1929] via-[#0a1929]/60 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="w-10 h-1 bg-[#c8102e] mb-4" />
                  <h3 className="font-black text-white text-xl leading-tight mb-3">{c.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed opacity-0 group-hover:opacity-100 max-h-0 group-hover:max-h-40 overflow-hidden transition-all duration-500">
                    {c.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== INDUSTRIES ============== */}
      <section className="py-24 bg-[#0a1929] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[#ff6b7a] font-bold text-xs uppercase tracking-[0.3em] mb-4">Industries</div>
            <h2 className="font-black text-4xl lg:text-5xl leading-tight tracking-tight mb-5">Sectors We Power</h2>
            <p className="text-slate-400 leading-relaxed">
              {home.industriesIntro || "Engineered for the demands of automotive, electronics, pharma, and heavy manufacturing."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => {
              const Icon = [Factory, Cpu, HeartPulse, Building2][i] || Factory;
              return (
                <div key={ind.name} className="bg-[#14213d] hover:bg-[#1a2b4a] transition-colors border border-white/5 group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={ind.image} alt={ind.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon className="w-6 h-6 text-[#c8102e]" />
                      <h3 className="font-black text-xl">{ind.name}</h3>
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-300 mb-5">
                      {ind.services.slice(0, 3).map((sv, si) => (
                        <li key={si} className="flex gap-2"><span className="text-[#c8102e]">•</span> {sv}</li>
                      ))}
                    </ul>
                    <div className="pt-4 border-t border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Clients</div>
                      <div className="text-sm font-semibold text-white">{ind.clients}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== TRUSTED BY / CLIENTS ============== */}
      <section className="py-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-3">Trusted Partnerships</div>
            <h2 className="font-black text-3xl lg:text-4xl text-[#0a1929] tracking-tight">Trusted by Industry Leaders</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-slate-200 border border-slate-200">
            {clients.map((c) => (
              <div key={c} className="bg-white h-24 flex items-center justify-center px-4 hover:bg-slate-50 transition-colors">
                <span className="font-black text-slate-400 hover:text-[#0a1929] transition-colors text-sm md:text-base tracking-tight text-center">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== WHY HIGA ============== */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[#c8102e] font-bold text-xs uppercase tracking-[0.3em] mb-4">Why HIGA</div>
            <h2 className="font-black text-4xl lg:text-5xl text-[#0a1929] tracking-tight leading-tight mb-5">One Team. One Goal.</h2>
            <p className="text-slate-600 italic">{home.whyUsIntro || "We don’t just sell solutions — we become your long-term engineering partner."}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUs.map((w, i) => {
              const icons = [Clock, Cog, Users, ShieldCheck, TrendingUp, Award];
              const Icon = icons[i];
              return (
                <div key={w.title} className="bg-white p-8 border-t-4 border-transparent hover:border-[#c8102e] hover:shadow-lg transition-all">
                  <Icon className="w-9 h-9 text-[#c8102e] mb-5" strokeWidth={1.5} />
                  <h3 className="font-black text-[#0a1929] text-lg mb-3 tracking-tight">{w.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{w.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== CTA STRIP ============== */}
      <section className="bg-[#c8102e] text-white py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="font-black text-3xl lg:text-4xl leading-tight tracking-tight mb-2">{home.ctaHeading || "Ready to Increase Productivity and Reduce Downtime?"}</h3>
            <p className="text-white/90">{home.ctaText || "Speak with our automation experts today and get a customised solution for your manufacturing facility."}</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#c8102e] hover:bg-slate-100 rounded-none px-8 py-6 text-sm font-bold tracking-wide uppercase gap-2">
                Get In Touch <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <a href={`tel:${company.phone || "+919896314582"}`}>
              <Button variant="outline" className="border-white bg-transparent text-white hover:bg-white hover:text-[#c8102e] rounded-none px-8 py-6 text-sm font-bold tracking-wide uppercase gap-2">
                <PhoneCall className="w-4 h-4" /> {company.phone || "+91 98963 14582"}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
